'use client';

// ── Profit Target · Funding — "$50,000 account" example ───────────────────────
// Figma: Moldura 12071:59880 (desktop, 792×374) and 12471:5153 (mobile, 366×842).
// Best day ÷ consistency = profit needed → profit target, then the balance rail.
// Every number is an illustrative example; sizes are Figma px / 8 in rem.

import { ExampleLabel, FigureFrame } from '../primitives';

const ASSETS = '/general-rules-v3/profit-target';

const TEXT = 'mb-0 whitespace-nowrap text-[2rem] leading-[3rem]';

function Block({
  label,
  value,
  tone = 'gray',
  dataUi,
}: {
  label: string;
  value: string;
  tone?: 'gray' | 'green';
  dataUi: string;
}) {
  const green = tone === 'green';
  return (
    <div
      data-ui={dataUi}
      className={`flex w-full flex-col gap-[0.5rem] rounded-[0.25rem] px-[1.5rem] py-[2rem] lg:min-w-0 lg:flex-1 ${green ? 'bg-[#228e71]' : 'bg-[#f2f4f3]'}`}
    >
      <p className={`${TEXT} font-normal ${green ? 'text-white/[0.72]' : 'text-black/[0.64]'}`}>{label}</p>
      <p className={`mb-0 whitespace-nowrap text-[3.5rem] font-medium leading-[4.5rem] ${green ? 'text-white' : 'text-black'}`}>
        {value}
      </p>
    </div>
  );
}

function Operator({ children }: { children: React.ReactNode }) {
  return (
    <p className="mb-0 whitespace-nowrap text-center text-[2.5rem] font-normal leading-[3.5rem] text-black/[0.32] lg:w-[2rem] lg:shrink-0">
      {children}
    </p>
  );
}

export default function ProfitTargetFunding() {
  return (
    <FigureFrame data-ui="figure.profitTargetFunding" className="font-geist">
      <ExampleLabel>EXAMPLE · $50,000 ACCOUNT</ExampleLabel>

      {/* Como a meta nasce */}
      <div
        data-ui="figure.profitTargetFunding.formula"
        className="flex w-full flex-col items-center gap-[1.5rem] lg:flex-row lg:items-stretch"
      >
        <Block dataUi="figure.profitTargetFunding.block-1" label="Best day" value="$1,000" />
        <Operator>÷</Operator>
        <Block dataUi="figure.profitTargetFunding.block-2" label="Consistency" value="25%" />
        <Operator>=</Operator>
        <Block dataUi="figure.profitTargetFunding.block-3" label="Profit needed" value="$4,000" />
        <Operator>
          <span className="lg:hidden">↓</span>
          <span className="hidden lg:inline">→</span>
        </Operator>
        <Block dataUi="figure.profitTargetFunding.block-4" label="Profit target" value="$54,000" tone="green" />
      </div>

      {/* Trilho do saldo */}
      <div data-ui="figure.profitTargetFunding.rail" className="flex w-full flex-col gap-[1.5rem]">
        <div className="flex w-full items-end justify-between">
          <div className="flex flex-col gap-[0.25rem]">
            <p className={`${TEXT} font-normal text-black/[0.64]`}>Balance</p>
            <p className="mb-0 whitespace-nowrap text-[2.5rem] font-medium leading-[3.5rem] text-black">$52,500</p>
          </div>
          <div
            data-ui="figure.profitTargetFunding.chip"
            className="flex shrink-0 items-center rounded-[0.25rem] bg-[#ac0003]/[0.08] px-[1.25rem] py-[0.25rem]"
          >
            <p className={`${TEXT} font-medium text-[#ac0003]`}>$1,500 still needed</p>
          </div>
        </div>
        <div data-ui="figure.profitTargetFunding.bar" className="relative h-[1rem] w-full rounded-[0.25rem] bg-black/[0.08]">
          {/* Figma draws the fill at 305.133/318 on mobile and 408.494/744 on desktop */}
          <div
            data-ui="figure.profitTargetFunding.bar-fill"
            className="absolute left-0 top-0 h-[1rem] w-[95.95%] rounded-[0.25rem] bg-[#228e71] lg:w-[54.9%]"
          >
            <img
              alt=""
              src={`${ASSETS}/funding-dot.svg`}
              className="absolute right-0 top-[-0.375rem] h-[1.75rem] w-[1.75rem] max-w-none translate-x-1/2"
            />
          </div>
        </div>
        <div className="flex w-full items-start justify-between">
          <p className={`${TEXT} font-normal text-black/40`}>$50,000 start</p>
          <p className={`${TEXT} font-medium text-[#228e71]`}>$54,000 target</p>
        </div>
      </div>

      <p data-ui="figure.profitTargetFunding.note" className="mb-0 text-[2rem] font-normal leading-[3rem] text-black/[0.64]">
        Profit target = best day $1,000 ÷ 25% consistency = $4,000 profit → $54,000
      </p>
    </FigureFrame>
  );
}
